import { defineStore } from 'pinia'
import {
  login as loginApi,
  loginByPassword as loginByPasswordApi,
  register as registerApi,
  updateProfile as updateProfileApi,
  changePassword as changePasswordApi,
  getUserInfo,
  logout as logoutApi,
} from '@/api/modules/user'
import {
  setTokenPair,
  clearAuthSession,
  getToken,
  getRefreshToken,
  hasStoredSession,
} from '@/utils/auth'
import { USER_INFO_KEY } from '@/config/constants'
import { getStorage, setStorage, removeStorage } from '@/utils/storage'
import { renewSessionIfNeeded } from '@/utils/session'
import type { ChangePasswordReq, LoginByPasswordReq, LoginReq, RegisterReq, UpdateProfileReq, UserInfo } from '@/api/types/user'

interface UserState {
  token: string
  hasRefresh: boolean
  userInfo: UserInfo | null
  /** 启动续期是否已完成（避免闪「未登录」） */
  sessionReady: boolean
}

function readCachedUserInfo(): UserInfo | null {
  return getStorage<UserInfo>(USER_INFO_KEY)
}

export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    token: getToken(),
    hasRefresh: !!getRefreshToken(),
    userInfo: readCachedUserInfo(),
    sessionReady: !hasStoredSession(),
  }),

  getters: {
    isLogin: (state): boolean => !!state.token || state.hasRefresh,
    nickname: (state): string => state.userInfo?.nickname ?? '未登录',
  },

  actions: {
    syncFromStorage(): void {
      this.token = getToken()
      this.hasRefresh = !!getRefreshToken()
      if (!this.userInfo) {
        this.userInfo = readCachedUserInfo()
      }
    },

    resetLocalSession(): void {
      this.token = ''
      this.hasRefresh = false
      this.userInfo = null
      clearAuthSession()
    },

    /** App 启动：Refresh 续期 + 拉用户信息（类似原生 App 自动登录） */
    async restoreSession(): Promise<boolean> {
      if (!hasStoredSession()) {
        this.resetLocalSession()
        this.sessionReady = true
        return false
      }

      this.syncFromStorage()

      try {
        const alive = await renewSessionIfNeeded()
        if (!alive) {
          this.resetLocalSession()
          return false
        }

        this.token = getToken()
        this.hasRefresh = !!getRefreshToken()

        await this.fetchUserInfo({ silent: true })
        return true
      } catch (err) {
        console.error('[user:restoreSession]', err)
        this.resetLocalSession()
        return false
      } finally {
        this.sessionReady = true
      }
    },

    persistUserInfo(info: UserInfo | null): void {
      this.userInfo = info
      if (info) {
        setStorage(USER_INFO_KEY, info)
      } else {
        removeStorage(USER_INFO_KEY)
      }
    },

    async login(payload: LoginReq): Promise<void> {
      const res = await loginApi(payload)
      this.token = res.token
      this.hasRefresh = !!res.refreshToken
      setTokenPair(res.token, res.refreshToken)
      this.sessionReady = true
      await this.fetchUserInfo()
    },

    async loginByPassword(payload: LoginByPasswordReq): Promise<void> {
      const res = await loginByPasswordApi(payload)
      this.token = res.token
      this.hasRefresh = !!res.refreshToken
      setTokenPair(res.token, res.refreshToken)
      this.sessionReady = true
      await this.fetchUserInfo()
    },

    async register(payload: RegisterReq): Promise<void> {
      const res = await registerApi(payload)
      this.token = res.token
      this.hasRefresh = !!res.refreshToken
      setTokenPair(res.token, res.refreshToken)
      this.sessionReady = true
      await this.fetchUserInfo()
    },

    async fetchUserInfo(options?: { silent?: boolean }): Promise<void> {
      const info = await getUserInfo({ silent: options?.silent })
      this.persistUserInfo(info)
    },

    async updateProfile(payload: UpdateProfileReq): Promise<void> {
      this.persistUserInfo(await updateProfileApi(payload))
    },

    async changePassword(payload: ChangePasswordReq): Promise<void> {
      await changePasswordApi(payload)
    },

    async logout(): Promise<void> {
      try {
        await logoutApi()
      } finally {
        this.resetLocalSession()
        this.sessionReady = true
      }
    },
  },
})
